import { COLORS, DRIVER_STATUS_COLORS } from "config/theme/baseTheme";
import { generateStatusColors } from "utils/colorUtils";

const DriverLocationConstants = {
  DRIVER_LOCATION_CONSTANTS: {
    MAP_OVERLAY_DEFAULT_MESSAGE: 'Select a hub and apply filters to view driver locations on the map',

    DRIVER_DETAIL_LABELS: {
      LOCATION: 'Location',
      BATTERY: 'Battery',
      ACTIVITY: 'Activity',
      CHECK_IN: 'Check-in',
      SPEED: 'Speed',
      DISTANCE_TO_HUB: 'Distance to Hub',
      VEHICLE: 'Vehicle',
      CONTACT: 'Contact Number',
      ASSIGNMENTS: 'Assignments',
      STATUS: 'Status',
    },

    // SelectBox items for Driver Locations filters
    DELIVERY_TYPE_OPTIONS: [
      { name: 'Express', value: 'EXPRESS' },
      { name: 'Standard', value: 'STANDARD' }
    ],

    DRIVER_STATUS_OPTIONS: [
      { name: 'Available', value: 'AVAILABLE' },
      { name: 'Assigned', value: 'ASSIGNED' },
      { name: 'Out for delivery', value: 'OUT_FOR_DELIVERY' },
      { name: 'Back to store', value: 'BACK_TO_STORE' },
      { name: 'Active', value: 'ACTIVE' },
      { name: 'Offline', value: 'OFFLINE' },
      { name: 'Away', value: 'AWAY' }
    ],

    // Default selected statuses for multi-select
    DEFAULT_SELECTED_STATUSES: ['AVAILABLE', 'ASSIGNED', 'OUT_FOR_DELIVERY'],
    
    // Default delivery type
    DEFAULT_DELIVERY_TYPE: 'EXPRESS',

    // Status color coding for visual differentiation
    // Using predefined driver status colors from base theme with auto-generated light/dark variants
    STATUS_COLORS: {
      'ACTIVE': generateStatusColors(DRIVER_STATUS_COLORS.ACTIVE),                 // OCEAN_GREEN
      'AVAILABLE': generateStatusColors(DRIVER_STATUS_COLORS.AVAILABLE),           // SEA_GREEN
      'ASSIGNED': generateStatusColors(DRIVER_STATUS_COLORS.ASSIGNED),             // GOLDEN_BELL
      'BACK_TO_STORE': generateStatusColors(DRIVER_STATUS_COLORS.BACK_TO_STORE),   // INFO_BLUE
      'OUT_FOR_DELIVERY': generateStatusColors(DRIVER_STATUS_COLORS.OUT_FOR_DELIVERY), // ORANGE
      'AWAY': generateStatusColors(DRIVER_STATUS_COLORS.AWAY),                     // GREY_SCALE
      'OFFLINE': generateStatusColors(DRIVER_STATUS_COLORS.OFFLINE)                // GREY_SCALE
    },

    // Status priority for sorting/filtering (higher number = higher priority)
    STATUS_PRIORITY: {
      'OUT_FOR_DELIVERY': 5,
      'ASSIGNED': 4,
      'AVAILABLE': 3,
      'ACTIVE': 2,
      'BACK_TO_STORE': 2,
      'AWAY': 1,
      'OFFLINE': 0
    },

    BATTERY_COLORS: {
      'GREEN': COLORS.SEA_GREEN,
      'ORANGE': COLORS.ORANGE,
      'RED': COLORS.DARK_RED 
    },

    // Country-wise default map configurations
    COUNTRY_MAP_CONFIGS: {
      'AE': { // United Arab Emirates
        center: { lat: 25.2048, lng: 55.2708 }, // Dubai center
        zoom: 10,
        name: 'United Arab Emirates'
      },
      'QA': { // Qatar
        center: { lat: 25.3548, lng: 51.1839 }, // Doha center
        zoom: 11,
        name: 'Qatar'
      },
      'SA': { // Saudi Arabia
        center: { lat: 24.7136, lng: 46.6753 }, // Riyadh center
        zoom: 6,
        name: 'Saudi Arabia'
      },
      'PK': { // Pakistan
        center: { lat: 30.3753, lng: 69.3451 }, // Pakistan center
        zoom: 6,
        name: 'Pakistan'
      },
      'KW': { // Kuwait
        center: { lat: 29.3117, lng: 47.4818 }, // Kuwait City center
        zoom: 11,
        name: 'Kuwait'
      },
      'EG': { // Egypt
        center: { lat: 30.0444, lng: 31.2357 }, // Cairo center
        zoom: 7,
        name: 'Egypt'
      },
      'JO': { // Jordan
        center: { lat: 31.9539, lng: 35.9106 }, // Amman center
        zoom: 9,
        name: 'Jordan'
      },
      'OM': { // Oman
        center: { lat: 23.5859, lng: 58.4059 }, // Muscat center
        zoom: 7,
        name: 'Oman'
      },
      'BH': { // Bahrain
        center: { lat: 26.0667, lng: 50.5577 }, // Manama center
        zoom: 12,
        name: 'Bahrain'
      },
      'LB': { // Lebanon
        center: { lat: 33.8547, lng: 35.8623 }, // Beirut center
        zoom: 10,
        name: 'Lebanon'
      },
      'KE': { // Kenya
        center: { lat: -1.2921, lng: 36.8219 }, // Nairobi center
        zoom: 7,
        name: 'Kenya'
      },
      'GE': { // Georgia
        center: { lat: 41.7151, lng: 44.8271 }, // Tbilisi center
        zoom: 8,
        name: 'Georgia'
      },
      'DEFAULT': { // Fallback for unknown countries
        center: { lat: 25.2048, lng: 55.2708 }, // Dubai as default
        zoom: 8,
        name: 'Default'
      }
    }
  },
};

// Utility function to get status colors
const getStatusColors = (status: string) => {
  const colors = DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.STATUS_COLORS;
  return colors[status as keyof typeof colors] || colors['OFFLINE'];
};

// Utility function to get status badge colors (for backward compatibility)
const getStatusBadgeColor = (status: string) => {
  return getStatusColors(status);
};

// Utility function to get status background color
const getStatusBgColor = (status: string) => {
  return getStatusColors(status).bg;
};

// Utility function to get status marker color
const getStatusMarkerColor = (status: string) => {
  return getStatusColors(status).marker;
};

// Utility function to get country map configuration
const getCountryMapConfig = (countryCode: string) => {
  const configs = DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.COUNTRY_MAP_CONFIGS;
  return configs[countryCode as keyof typeof configs] || configs['DEFAULT'];
};

export { getStatusBadgeColor, getStatusColors, getStatusBgColor, getStatusMarkerColor, getCountryMapConfig };
export default DriverLocationConstants;